<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h2 class="page-title">{{ t('users_management') }}</h2>
                <p class="page-subtitle">{{ t('users_management_subtitle') }}</p>
            </div>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="statusFilter" class="status-select" :disabled="loading">
                        <option value="all">{{ t('all') }}</option>
                        <option value="active">{{ t('active') }}</option>
                        <option value="inactive">{{ t('inactive') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <div class="columns-wrap">
                        <button type="button" class="tool-btn tool-btn-light" @click="toggleColumns" :disabled="loading">COLUMNS</button>
                        <div v-if="showColumns" class="columns-dropdown">
                            <label v-for="c in columns" :key="c.key" class="columns-item">
                                <input type="checkbox" v-model="visibleColumns[c.key]" :disabled="c.locked">
                                <span>{{ t(c.labelKey) }}</span>
                            </label>
                        </div>
                    </div>

                    <button type="button" class="tool-btn tool-btn-primary" @click="loadUsers" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                    <button v-if="permissionsLoaded && canCreateUsers" type="button" class="tool-btn tool-btn-success" @click="openCreateModal" :disabled="loading">{{ t('create') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" class="search-input" :placeholder="t('search_users') + '...'" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="visibleColumns.id">
                                <input v-if="showFilters" v-model="colFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th v-if="visibleColumns.login_id">
                                <input v-if="showFilters" v-model="colFilters.login_id" type="text" class="th-input" :placeholder="t('login_id')">
                                <span v-else>{{ t('login_id') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer_name">
                                <input v-if="showFilters" v-model="colFilters.name" type="text" class="th-input" :placeholder="t('customer_name')">
                                <span v-else>{{ t('customer_name') }}</span>
                            </th>
                            <th v-if="visibleColumns.email">
                                <input v-if="showFilters" v-model="colFilters.email" type="text" class="th-input" :placeholder="t('email')">
                                <span v-else>{{ t('email') }}</span>
                            </th>
                            <th v-if="visibleColumns.user_type">
                                <input v-if="showFilters" v-model="colFilters.user_type" type="text" class="th-input" :placeholder="t('user_type')">
                                <span v-else>{{ t('user_type') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer_id">
                                <input v-if="showFilters" v-model="colFilters.customer_id" type="text" class="th-input" :placeholder="t('customer_id')">
                                <span v-else>{{ t('customer_id') }}</span>
                            </th>
                            <th v-if="visibleColumns.mobile">
                                <input v-if="showFilters" v-model="colFilters.mobile_no" type="text" class="th-input" :placeholder="t('mobile')">
                                <span v-else>{{ t('mobile') }}</span>
                            </th>
                            <th v-if="visibleColumns.status">
                                <select v-if="showFilters" v-model="colFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="active">{{ t('active') }}</option>
                                    <option value="inactive">{{ t('inactive') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th v-if="visibleColumns.pass_reset">
                                <select v-if="showFilters" v-model="colFilters.pass_reset" class="th-select">
                                    <option value="">{{ t('pass_reset') }}</option>
                                    <option value="yes">{{ t('yes') }}</option>
                                    <option value="no">{{ t('no') }}</option>
                                </select>
                                <span v-else>{{ t('pass_reset') }}</span>
                            </th>
                            <th v-if="visibleColumns.last_login">{{ t('last_login') }}</th>
                            <th v-if="visibleColumns.roles">
                                <input v-if="showFilters" v-model="colFilters.role" type="text" class="th-input" :placeholder="t('roles')">
                                <span v-else>{{ t('roles') }}</span>
                            </th>
                            <th v-if="visibleColumns.created">{{ t('created') }}</th>
                            <th v-if="visibleColumns.updated">{{ t('updated') }}</th>
                            <th v-if="visibleColumns.actions">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td :colspan="visibleColumnCount" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="user in filteredUsers" v-if="!loading" :key="user.id">
                            <td v-if="visibleColumns.id" class="mono">{{ user.id }}</td>
                            <td v-if="visibleColumns.login_id" class="mono">{{ user.login_id || '-' }}</td>
                            <td v-if="visibleColumns.customer_name">
                                <div class="cell-main">{{ user.name || '-' }}</div>
                                <div class="cell-sub mono">{{ user.mobile_no || '-' }}</div>
                            </td>
                            <td v-if="visibleColumns.email">{{ user.email || '-' }}</td>
                            <td v-if="visibleColumns.user_type">{{ user.user_type || '-' }}</td>
                            <td v-if="visibleColumns.customer_id" class="mono">{{ user.customer_id ?? '-' }}</td>
                            <td v-if="visibleColumns.mobile" class="mono">{{ user.mobile_no || '-' }}</td>
                            <td v-if="visibleColumns.status">
                                <span :class="['tag', user.status ? 'tag-success' : 'tag-gray']">
                                    {{ user.status ? t('active') : t('inactive') }}
                                </span>
                            </td>
                            <td v-if="visibleColumns.pass_reset">
                                <span :class="['tag', user.is_pass_reset_required ? 'tag-warning' : 'tag-gray']">
                                    {{ user.is_pass_reset_required ? t('yes') : t('no') }}
                                </span>
                            </td>
                            <td v-if="visibleColumns.last_login">{{ formatDateTime(user.last_login_at) }}</td>
                            <td v-if="visibleColumns.roles">
                                <div class="tags">
                                    <span v-for="role in user.roles" :key="role.id" class="tag tag-primary">
                                        {{ role.name }}
                                    </span>
                                    <span v-if="user.roles.length === 0" class="tag tag-gray">{{ t('no_role') }}</span>
                                </div>
                            </td>
                            <td v-if="visibleColumns.created">{{ formatDate(user.created_at) }}</td>
                            <td v-if="visibleColumns.updated">{{ formatDate(user.updated_at) }}</td>
                            <td v-if="visibleColumns.actions">
                                <div class="action-buttons">
                                    <button
                                        v-if="permissionsLoaded && canViewUsers"
                                        @click="viewUser(user)"
                                        class="icon-btn icon-btn-light"
                                        :title="t('view')"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M1.66667 10C1.66667 10 4.58333 4.16667 10 4.16667C15.4167 4.16667 18.3333 10 18.3333 10C18.3333 10 15.4167 15.8333 10 15.8333C4.58333 15.8333 1.66667 10 1.66667 10Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <button 
                                        v-if="permissionsLoaded && canEditUsers" 
                                        @click="editUser(user)" 
                                        class="icon-btn icon-btn-primary" 
                                        :title="t('edit')">
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M11.6667 2.5L15.8333 6.66667M13.3333 1.66667L17.5 5.83333L10.8333 12.5H6.66667V8.33333L13.3333 1.66667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M2.5 18.3333H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                        </svg>
                                    </button>
                                    <button 
                                        v-if="permissionsLoaded && canDeleteUsers" 
                                        @click="deleteUser(user)" 
                                        class="icon-btn icon-btn-danger" 
                                        :title="t('delete')">
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M15.8333 5V16.6667C15.8333 17.5872 15.0872 18.3333 14.1667 18.3333H5.83333C4.91286 18.3333 4.16667 17.5872 4.16667 16.6667V5M12.5 5V3.33333C12.5 2.41286 11.7538 1.66667 10.8333 1.66667H9.16667C8.24619 1.66667 7.5 2.41286 7.5 3.33333V5M2.5 5H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <span v-if="permissionsLoaded && !canEditUsers && !canDeleteUsers" class="no-access-text">{{ t('view_only') }}</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!loading && filteredUsers.length === 0">
                            <td :colspan="visibleColumnCount" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">{{ showingText }}</div>
                <div class="pager-right">
                    <button class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                    <button v-for="p in pageButtons" :key="p" class="pager-num" :class="{ active: p === page }" @click="goPage(p)" :disabled="loading">
                        {{ p }}
                    </button>
                    <button class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <!-- Create/Edit Modal -->
        <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ viewOnly ? t('user_details') : (editingUser ? t('edit_user') : t('create_new_user')) }}</h3>
                    <button @click="closeModal" class="close-btn">
                        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <form @submit.prevent="saveUser" class="modal-body">
                    <div class="form-group">
                        <label>{{ t('login_id') }}</label>
                        <input v-model="form.login_id" type="text" class="form-input" maxlength="25" :disabled="viewOnly">
                    </div>
                    <div class="form-group">
                        <label>{{ t('customer_name') }}</label>
                        <input v-model="form.name" type="text" required class="form-input" :disabled="viewOnly">
                    </div>
                    <div class="form-group">
                        <label>{{ t('email') }}</label>
                        <input v-model="form.email" type="email" required class="form-input" :disabled="viewOnly">
                    </div>
                    <div class="form-group">
                        <label>{{ t('user_type') }}</label>
                        <input v-model="form.user_type" type="text" class="form-input" maxlength="35" :disabled="viewOnly">
                    </div>
                    <div class="form-group">
                        <label>{{ t('customer_id') }}</label>
                        <input v-model.number="form.customer_id" type="number" min="0" class="form-input" :disabled="viewOnly">
                    </div>
                    <div class="form-group">
                        <label>{{ t('mobile_no') }}</label>
                        <input v-model="form.mobile_no" type="text" class="form-input" maxlength="35" :disabled="viewOnly">
                    </div>
                    <div class="form-group">
                        <label>{{ t('status') }}</label>
                        <select v-model="form.status" class="form-input" :disabled="viewOnly">
                            <option :value="true">{{ t('active') }}</option>
                            <option :value="false">{{ t('inactive') }}</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>{{ t('is_pass_reset_required') }}</label>
                        <select v-model="form.is_pass_reset_required" class="form-input" :disabled="viewOnly">
                            <option :value="false">{{ t('no') }}</option>
                            <option :value="true">{{ t('yes') }}</option>
                        </select>
                    </div>
                    <div v-if="!viewOnly" class="form-group">
                        <label>{{ t('password') }}</label>
                        <input 
                            v-model="form.password" 
                            type="password" 
                            :required="!editingUser" 
                            class="form-input"
                            :placeholder="t('enter_new_password')"
                        >
                        <small v-if="editingUser" class="form-hint">{{ t('password_leave_blank') }}</small>
                    </div>
                    <div v-if="!viewOnly && (form.password || !editingUser)" class="form-group">
                        <label>{{ t('confirm_password') }}</label>
                        <input 
                            v-model="form.password_confirmation" 
                            type="password" 
                            :required="!editingUser || form.password" 
                            class="form-input"
                            :placeholder="t('confirm_password_placeholder')"
                        >
                    </div>
                    <div class="form-group">
                        <label>{{ t('roles') }}</label>
                        <div class="checkbox-group">
                            <label v-for="role in availableRoles" :key="role.id" class="checkbox-label">
                                <input type="checkbox" :value="role.id" v-model="form.roles" :disabled="viewOnly">
                                <span>{{ role.name }}</span>
                            </label>
                        </div>
                    </div>
                    <div v-if="editingUser" class="form-group">
                        <label>{{ t('last_login_date') }}</label>
                        <input :value="formatDateTime(editingUser.last_login_at)" type="text" class="form-input" disabled>
                    </div>
                    <div v-if="editingUser" class="form-group">
                        <label>{{ t('create_date') }}</label>
                        <input :value="formatDateTime(editingUser.created_at)" type="text" class="form-input" disabled>
                    </div>
                    <div v-if="editingUser" class="form-group">
                        <label>{{ t('update_date') }}</label>
                        <input :value="formatDateTime(editingUser.updated_at)" type="text" class="form-input" disabled>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="closeModal" class="btn btn-secondary">{{ t('cancel') }}</button>
                        <button v-if="!viewOnly" type="submit" class="btn btn-primary">{{ t('save') }}</button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Toast Notifications -->
        <Teleport to="body">
            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-icon">
                            <svg v-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M16.6667 5L7.50002 14.1667L3.33335 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <svg v-else-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { ref, computed, onMounted, inject, Teleport, TransitionGroup, watch } from 'vue';
import axios from 'axios';

export default {
    name: 'UsersList',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const t = (key) => adminT(key);

        const users = ref([]);
        const availableRoles = ref([]);
        const searchQuery = ref('');
        const loading = ref(false);
        const page = ref(1);
        const perPage = ref(20);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const statusFilter = ref('all');
        let searchTimer = null;
        const showModal = ref(false);
        const viewOnly = ref(false);
        const editingUser = ref(null);
        const toasts = ref([]);
        const permissions = ref([]);
        const permissionsLoaded = ref(false);
        const showColumns = ref(false);
        const showFilters = ref(false);
        const colFilters = ref({
            id: '',
            login_id: '',
            name: '',
            email: '',
            user_type: '',
            customer_id: '',
            mobile_no: '',
            status: '',
            pass_reset: '',
            role: '',
        });
        const columns = [
            { key: 'id', labelKey: 'id' },
            { key: 'login_id', labelKey: 'login_id' },
            { key: 'customer_name', labelKey: 'customer_name' },
            { key: 'email', labelKey: 'email' },
            { key: 'user_type', labelKey: 'user_type' },
            { key: 'customer_id', labelKey: 'customer_id' },
            { key: 'mobile', labelKey: 'mobile' },
            { key: 'status', labelKey: 'status' },
            { key: 'pass_reset', labelKey: 'pass_reset' },
            { key: 'last_login', labelKey: 'last_login' },
            { key: 'roles', labelKey: 'roles' },
            { key: 'created', labelKey: 'created' },
            { key: 'updated', labelKey: 'updated' },
            { key: 'actions', labelKey: 'actions', locked: true },
        ];
        const visibleColumns = ref({
            id: true,
            login_id: true,
            customer_name: true,
            email: true,
            user_type: true,
            customer_id: true,
            mobile: true,
            status: true,
            pass_reset: true,
            last_login: true,
            roles: true,
            created: true,
            updated: true,
            actions: true,
        });
        const form = ref({
            login_id: '',
            name: '',
            email: '',
            user_type: '',
            customer_id: null,
            password: '',
            password_confirmation: '',
            is_pass_reset_required: false,
            mobile_no: '',
            status: true,
            roles: []
        });
        
        const loadPermissions = async () => {
            try {
                const response = await axios.get('/admin/api/user-permissions');
                const loadedPermissions = Array.isArray(response.data.permissions) ? response.data.permissions : [];
                permissions.value = loadedPermissions;
                permissionsLoaded.value = true;
            } catch (error) {
                permissions.value = [];
                permissionsLoaded.value = true;
            }
        };
        
        const hasPermission = (permission) => {
            if (!permissionsLoaded.value || !permissions.value?.length) return false;
            if (!permission) return true;
            if (permissions.value.includes('*')) return true;
            return permissions.value.some(p => 
                p && typeof p === 'string' && p.toLowerCase() === permission.toLowerCase()
            );
        };
        
        const hasAnyPermission = (patterns) => {
            if (!permissionsLoaded.value || !permissions.value?.length) return false;
            if (permissions.value.includes('*')) return true;
            
            const permsArray = Array.isArray(patterns) ? patterns : [patterns];
            return permissions.value.some(p => {
                if (!p || typeof p !== 'string') return false;
                const permLower = p.toLowerCase().trim();
                return permsArray.some(pattern => permLower === pattern.toLowerCase().trim());
            });
        };
        
        const canViewUsers = computed(() => {
            if (!permissionsLoaded.value) return false;
            return hasAnyPermission(['view-users', 'create-users', 'edit-users', 'edite-users', 'delete-users']);
        });
        
        const canEditUsers = computed(() => {
            if (!permissionsLoaded.value) return false;
            if (permissions.value?.includes('*')) return true;
            return hasAnyPermission(['edit-users', 'edite-users']);
        });
        
        const canDeleteUsers = computed(() => {
            if (!permissionsLoaded.value) return false;
            if (permissions.value?.includes('*')) return true;
            return hasAnyPermission(['delete-users']);
        });
        
        const canCreateUsers = computed(() => {
            if (!permissionsLoaded.value) return false;
            if (permissions.value?.includes('*')) return true;
            return hasAnyPermission(['create-users']);
        });

        const toggleColumns = () => {
            showColumns.value = !showColumns.value;
        };

        const visibleColumnCount = computed(() => {
            const v = visibleColumns.value || {};
            return columns.reduce((sum, c) => sum + (v[c.key] ? 1 : 0), 0) || 1;
        });

        const hasAnyFilter = computed(() => {
            const f = colFilters.value || {};
            return !!(
                (searchQuery.value || '').trim()
                || (statusFilter.value || 'all') !== 'all'
                || (f.id || '').trim()
                || (f.login_id || '').trim()
                || (f.name || '').trim()
                || (f.email || '').trim()
                || (f.user_type || '').trim()
                || (f.customer_id || '').trim()
                || (f.mobile_no || '').trim()
                || (f.status || '').trim()
                || (f.pass_reset || '').trim()
                || (f.role || '').trim()
            );
        });

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            statusFilter.value = 'all';
            colFilters.value = {
                id: '',
                login_id: '',
                name: '',
                email: '',
                user_type: '',
                customer_id: '',
                mobile_no: '',
                status: '',
                pass_reset: '',
                role: '',
            };
            page.value = 1;
            await loadUsers();
        };

        const normalizeSearch = (v) => (v ?? '').toString().toLowerCase().trim();

        const filteredUsers = computed(() => {
            const list = Array.isArray(users.value) ? users.value : [];
            const f = colFilters.value || {};
            const idQ = normalizeSearch(f.id);
            const loginQ = normalizeSearch(f.login_id);
            const nameQ = normalizeSearch(f.name);
            const emailQ = normalizeSearch(f.email);
            const typeQ = normalizeSearch(f.user_type);
            const customerQ = normalizeSearch(f.customer_id);
            const mobileQ = normalizeSearch(f.mobile_no);
            const statusQ = normalizeSearch(f.status);
            const passQ = normalizeSearch(f.pass_reset);
            const roleQ = normalizeSearch(f.role);

            return list.filter((u) => {
                if (idQ && !normalizeSearch(u.id).includes(idQ)) return false;
                if (loginQ && !normalizeSearch(u.login_id).includes(loginQ)) return false;
                if (nameQ && !normalizeSearch(u.name).includes(nameQ)) return false;
                if (emailQ && !normalizeSearch(u.email).includes(emailQ)) return false;
                if (typeQ && !normalizeSearch(u.user_type).includes(typeQ)) return false;
                if (customerQ && !normalizeSearch(u.customer_id).includes(customerQ)) return false;
                if (mobileQ && !normalizeSearch(u.mobile_no).includes(mobileQ)) return false;
                if (statusQ === 'active' && !u.status) return false;
                if (statusQ === 'inactive' && !!u.status) return false;
                if (passQ === 'yes' && !u.is_pass_reset_required) return false;
                if (passQ === 'no' && !!u.is_pass_reset_required) return false;
                if (roleQ) {
                    const roleNames = (u.roles || []).map((r) => normalizeSearch(r?.name)).filter(Boolean);
                    if (!roleNames.some((x) => x.includes(roleQ))) return false;
                }
                return true;
            });
        });

        const loadUsers = async () => {
            try {
                loading.value = true;
                const response = await axios.get('/admin/api/users', {
                    params: {
                        page: page.value,
                        per_page: perPage.value,
                        status: statusFilter.value,
                        q: searchQuery.value || '',
                    },
                });

                const payload = response.data || {};
                const items = Array.isArray(payload.data) ? payload.data : [];
                const nextPageValue = Number(payload.current_page || page.value || 1);
                const nextPerPage = Number(payload.per_page || perPage.value || 20);
                const nextTotal = Number(payload.total || 0);

                users.value = items;
                pagination.value = { page: nextPageValue, per_page: nextPerPage, total: nextTotal };
                page.value = nextPageValue;
                perPage.value = nextPerPage;
            } catch (error) {
                showNotification('Error loading users: ' + (error.response?.data?.message || error.message), 'error');
            } finally {
                loading.value = false;
            }
        };

        const loadRoles = async () => {
            try {
                const response = await axios.get('/admin/api/roles', { params: { per_page: 100 } });
                const payload = response.data || {};
                availableRoles.value = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
            } catch (error) {
                // Silent fail - roles are optional
            }
        };

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const max = 5;
            let start = Math.max(1, current - Math.floor(max / 2));
            let end = start + max - 1;
            if (end > total) {
                end = total;
                start = Math.max(1, end - max + 1);
            }
            const out = [];
            for (let p = start; p <= end; p++) out.push(p);
            return out;
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;

            if (!hasAnyFilter.value) return base;
            const filteredTpl = t('filtered');
            const suffix = filteredTpl && filteredTpl !== 'filtered' && filteredTpl.includes('{count}')
                ? filteredTpl.replace('{count}', String(filteredUsers.value.length))
                : `(filtered: ${filteredUsers.value.length})`;
            return `${base} ${suffix}`;
        });

        const goPage = async (p) => {
            const next = Math.min(Math.max(1, Number(p || 1)), totalPages.value);
            if (next === page.value) return;
            page.value = next;
            await loadUsers();
        };

        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await loadUsers();
        };

        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await loadUsers();
        };

        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await loadUsers();
        };

        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await loadUsers();
        };

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                page.value = 1;
                loadUsers();
            }, 350);
        };

        watch(perPage, async () => {
            page.value = 1;
            await loadUsers();
        });

        watch(statusFilter, async () => {
            page.value = 1;
            await loadUsers();
        });

        const openCreateModal = () => {
            // Check permission before allowing create
            if (!permissionsLoaded.value) {
                showNotification('Please wait, permissions are loading...', 'error');
                return;
            }
            
            if (!canCreateUsers.value) {
                showNotification('You do not have permission to create users', 'error');
                return;
            }
            
            viewOnly.value = false;
            editingUser.value = null;
            form.value = {
                login_id: '',
                name: '',
                email: '',
                user_type: '',
                customer_id: null,
                mobile_no: '',
                status: true,
                is_pass_reset_required: false,
                password: '',
                password_confirmation: '',
                roles: []
            };
            showModal.value = true;
        };

        const editUser = (user) => {
            if (!permissionsLoaded.value) {
                showNotification('Please wait, permissions are loading...', 'error');
                return;
            }
            
            if (!canEditUsers.value) {
                showNotification('You do not have permission to edit users', 'error');
                return;
            }
            
            viewOnly.value = false;
            editingUser.value = user;
            form.value = {
                login_id: user.login_id || '',
                name: user.name || '',
                email: user.email || '',
                user_type: user.user_type || '',
                customer_id: user.customer_id ?? null,
                mobile_no: user.mobile_no || '',
                status: !!user.status,
                is_pass_reset_required: !!user.is_pass_reset_required,
                password: '',
                password_confirmation: '',
                roles: user.roles?.map(r => r.id) || []
            };
            showModal.value = true;
        };

        const viewUser = (user) => {
            if (!permissionsLoaded.value) {
                showNotification('Please wait, permissions are loading...', 'error');
                return;
            }
            if (!canViewUsers.value) {
                showNotification('You do not have permission to view users', 'error');
                return;
            }

            viewOnly.value = true;
            editingUser.value = user;
            form.value = {
                login_id: user.login_id || '',
                name: user.name || '',
                email: user.email || '',
                user_type: user.user_type || '',
                customer_id: user.customer_id ?? null,
                mobile_no: user.mobile_no || '',
                status: !!user.status,
                is_pass_reset_required: !!user.is_pass_reset_required,
                password: '',
                password_confirmation: '',
                roles: user.roles?.map(r => r.id) || [],
            };
            showModal.value = true;
        };

        const saveUser = async () => {
            if (viewOnly.value) return;
            try {
                const url = editingUser.value 
                    ? `/admin/users/${editingUser.value.id}`
                    : '/admin/users';
                const method = editingUser.value ? 'put' : 'post';
                
                // Prepare data - only include password if it's not empty (for edit)
                const data = { ...form.value };
                if (typeof data.customer_id === 'number' && Number.isNaN(data.customer_id)) {
                    data.customer_id = null;
                }
                
                // For edit: if password is empty, remove password fields
                if (editingUser.value && !data.password) {
                    delete data.password;
                    delete data.password_confirmation;
                }
                
                await axios[method](url, data);
                await loadUsers();
                closeModal();
                showNotification('User saved successfully!', 'success');
            } catch (error) {
                showNotification('Error saving user: ' + (error.response?.data?.message || error.message), 'error');
            }
        };

        const deleteUser = async (user) => {
            if (!confirm(`Are you sure you want to delete ${user.name}?`)) return;
            
            try {
                await axios.delete(`/admin/users/${user.id}`);
                await loadUsers();
                showNotification('User deleted successfully!', 'success');
            } catch (error) {
                showNotification('Error deleting user: ' + (error.response?.data?.message || error.message), 'error');
            }
        };

        const closeModal = () => {
            showModal.value = false;
            editingUser.value = null;
            viewOnly.value = false;
        };

        const showNotification = (message, type = 'success') => {
            const id = Date.now() + Math.random();
            const toast = {
                id,
                message,
                type
            };
            
            toasts.value.push(toast);
            
            // Auto remove after 4 seconds
            setTimeout(() => {
                removeToast(id);
            }, 4000);
        };

        const removeToast = (id) => {
            const index = toasts.value.findIndex(t => t.id === id);
            if (index > -1) {
                toasts.value.splice(index, 1);
            }
        };

        const getUserInitials = (name) => {
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        };

        const formatDate = (date) => {
            if (!date) return '-';
            return new Date(date).toLocaleDateString();
        };

        const formatDateTime = (date) => {
            if (!date) return '-';
            return new Date(date).toLocaleString();
        };

        onMounted(async () => {
            await loadPermissions();
            await loadRoles();
            await loadUsers();
        });

        return {
            t,
            users,
            loading,
            page,
            perPage,
            pagination,
            statusFilter,
            hasPermission,
            permissionsLoaded,
            canViewUsers,
            canEditUsers,
            canDeleteUsers,
            canCreateUsers,
            availableRoles,
            searchQuery,
            showColumns,
            showFilters,
            columns,
            visibleColumns,
            visibleColumnCount,
            colFilters,
            hasAnyFilter,
            toggleColumns,
            toggleFilters,
            clearFilters,
            filteredUsers,
            totalPages,
            pageButtons,
            showingText,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            debouncedSearch,
            loadUsers,
            showModal,
            viewOnly,
            editingUser,
            form,
            openCreateModal,
            viewUser,
            editUser,
            saveUser,
            deleteUser,
            closeModal,
            getUserInitials,
            formatDate,
            formatDateTime,
            toasts,
            removeToast
        };
    }
};
</script>

<style scoped>
.page {
    display: flex;
    flex-direction: column;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
}

.page-subtitle {
    font-size: 14px;
    color: #718096;
}

.btn {
    padding: 12px 24px;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
}

.btn-primary {
    background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.5);
}

.card {
    margin-top: 18px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.datatable-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.columns-wrap {
    position: relative;
}

.columns-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 12px;
    padding: 10px 10px;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.12);
    z-index: 40;
}

.columns-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 800;
    color: #0f172a;
    font-size: 12px;
}

.columns-item:hover {
    background: rgba(241, 245, 249, 0.8);
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.search-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
}

.data-table thead {
    background: #0b2c3d;
}

.data-table th {
    padding: 10px 8px;
    text-align: left;
    font-size: 12px;
    font-weight: 900;
    color: #ffffff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    vertical-align: top;
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.data-table td {
    padding: 9px 8px;
    border-top: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 13px;
    vertical-align: top;
    background: #ffffff;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.cell-main {
    font-weight: 600;
}

.cell-sub {
    color: #64748b;
    font-size: 12px;
    margin-top: 2px;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.data-table tbody tr:hover td {
    background: rgba(226, 232, 240, 0.55);
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-avatar-small {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
}

.user-name {
    font-weight: 600;
    color: #1a202c;
}

.tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.tag {
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.tag-primary {
    background: #e6f0ff;
    color: #4A90E2;
}

.tag-success {
    background: #c6f6d5;
    color: #22543d;
}

.tag-warning {
    background: #feebc8;
    color: #7b341e;
}

.tag-gray {
    background: #edf2f7;
    color: #718096;
}

.action-buttons {
    display: flex;
    gap: 8px;
}

.icon-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.icon-btn-primary {
    background: #e6f0ff;
    color: #4A90E2;
}

.icon-btn-primary:hover {
    background: #4A90E2;
    color: white;
}

.icon-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.icon-btn-light:hover {
    background: #0b2c3d;
    color: #ffffff;
}

.icon-btn-danger {
    background: #fed7d7;
    color: #e53e3e;
}

.icon-btn-danger:hover {
    background: #e53e3e;
    color: white;
}

.no-access-text {
    color: #718096;
    font-size: 12px;
    font-style: italic;
    padding: 8px;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 2px 10px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    padding: 7px 10px;
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;
    min-width: 66px;
}

.pager-num {
    min-width: 44px;
    height: 32px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    border-radius: 6px;
    font-weight: 900;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.modal-content {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    padding: 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h3 {
    font-size: 20px;
    font-weight: 700;
    color: #1a202c;
}

.close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f7fafc;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #718096;
    transition: all 0.2s;
}

.close-btn:hover {
    background: #edf2f7;
    color: #1a202c;
}

.modal-body {
    padding: 24px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #2d3748;
}

.form-input {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    transition: all 0.2s;
}

.form-input:focus {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.form-hint {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: #718096;
}

.checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #4a5568;
}

.checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #4A90E2;
}

.modal-footer {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.btn-secondary {
    background: #edf2f7;
    color: #4a5568;
}

.btn-secondary:hover {
    background: #e2e8f0;
}

/* Toast Notifications */
.toast-container {
    position: fixed !important;
    top: 20px !important;
    right: 20px !important;
    z-index: 99999 !important;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
}

.toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    min-width: 320px;
    max-width: 420px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
    pointer-events: all;
    position: relative;
    overflow: hidden;
}

.toast::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: currentColor;
}

.toast-success {
    border-left: 4px solid #48bb78;
    color: #22543d;
}

.toast-success .toast-icon {
    color: #48bb78;
}

.toast-error {
    border-left: 4px solid #f56565;
    color: #742a2a;
}

.toast-error .toast-icon {
    color: #f56565;
}

.toast-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #2d3748;
}

.toast-close {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0aec0;
    border-radius: 6px;
    transition: all 0.2s;
    padding: 0;
}

.toast-close:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #718096;
}

/* Toast Animations */
.toast-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-leave-active {
    transition: all 0.25s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-move {
    transition: transform 0.3s ease;
}

@media (max-width: 768px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
    
    .table-container {
        overflow-x: scroll;
    }
    
    .modal-content {
        width: 95%;
        margin: 20px;
    }
    
    .toast-container {
        top: 10px;
        right: 10px;
        left: 10px;
    }
    
    .toast {
        min-width: auto;
        max-width: 100%;
    }
}
</style>
